<template>
  <BottomDrawer
    :is-shown="state.isShown"
    @close="close"
  >
    <div class="content">
      <template v-if="payment.status === paymentStatuses.success">
        <div class="successIcon">
          <div class="gradient success"/>
        </div>

        <Typography
          variant="v20-700"
          center
          style="margin-top: 8px"
        >
          Оплачено
        </Typography>

        <Typography
          variant="v14-400"
          color="secondary"
          center
        >
          Чек отправлен на электронную почту {{ store.state.user.profile.email }}
        </Typography>
      </template>

      <template v-if="payment.status === paymentStatuses.pending">
        <div class="pendingIcon">
          <div class="gradient pending"/>
        </div>

        <Typography
          variant="v20-700"
          center
          style="margin-top: 8px"
        >
          Ожидаем подтверждение платежа
        </Typography>

        <Typography
          variant="v14-400"
          color="secondary"
          center
        >
          Это может занять некоторое время
        </Typography>
        <Typography
          variant="v14-400"
          color="secondary"
          center
        >
          После обработки платежа чек будет отправлен на электронную почту {{ store.state.user.profile.email }}
        </Typography>
      </template>

      <template v-if="payment.status === paymentStatuses.failed">
        <div class="failedIcon">
          <div class="gradient failed"/>
        </div>

        <Typography
          variant="v20-700"
          center
          style="margin-top: 8px"
        >
          Ошибка при обработке платежа
        </Typography>

        <Typography
          variant="v14-400"
          color="secondary"
          center
        >
          Убедись что на карте достаточно средств и попробуй ещё раз
        </Typography>
      </template>
    </div>

    <div class="modal-divider"/>

    <AppButton
      v-if="payment.status === paymentStatuses.success && tgChatLink"
      full-width
      style="margin: 16px 0"
      @click="handleButtonClick"
    >
      Перейти в ТГ чат подписки
    </AppButton>

    <AppButton
      v-if="payment.status === paymentStatuses.pending"
      full-width
      style="margin: 16px 0"
      :loading="state.subscriptionLoading"
      :disabled="state.subscriptionLoading"
      @click="checkSubscriptionStatus"
    >
      Проверить статус платежа
    </AppButton>

    <AppButton
      v-if="payment.status === paymentStatuses.failed"
      full-width
      style="margin: 16px 0"
      @click="handleFailedButtonClick"
    >
      Попробовать ещё раз
    </AppButton>

    <AppButton
      full-width
      style="margin: 16px 0"
      variant="secondary"
      @click="close"
    >
      Закрыть
    </AppButton>
  </BottomDrawer>
</template>

<script setup>
import {computed, onBeforeMount, reactive, watch} from 'vue'

import { appAxios } from '@/axios.js'
import { paymentStatuses } from '@/configs/subscriptionStatuses.js'
import { router } from '@/router/router.js'
import store from '@/store/store.js'

import AppButton from '@/components/UI/AppButton.vue'
import BottomDrawer from '@/components/BottomDrawer.vue'
import Typography from '@/components/UI/Typography.vue'

const SUBSCRIPTION_CONTROL_ROUTE = 'SubscriptionControl'

const props = defineProps({
  data: {
    type: Object,
    default: {},
  }
})

const paymentId = computed(() =>
  props.data?.query?.payment_id
  || store.state.app.messageComponentData?.query?.payment_id,
)

const state = reactive({
  isShown: false,
  subscriptionLoading: false,
})

const userProfile = computed(() => {
  return store.state.user?.profile
})
const subscriptions = computed(() => {
  return userProfile.value?.subscriptions || []
})

const matchedPaymentUpdate = computed(() => {
  const id = paymentId.value
  const paymentUpdate = store.state.user.paymentUpdate

  if (
    paymentUpdate?.payment != null
    && id != null
    && String(paymentUpdate.payment.id) === String(id)
  ) {
    return paymentUpdate
  }

  return null
})

const paymentMatch = computed(() => {
  const id = paymentId.value
  if (id == null) {
    return null
  }

  for (const subscription of subscriptions.value) {
    const found = subscription.payments?.find(p => String(p.id) === String(id))
    if (found) {
      return { payment: found, subscription }
    }
  }

  return null
})

/** Сначала WS payment_update по payment_id, иначе платёж из profile.subscriptions */
const payment = computed(() => {
  return matchedPaymentUpdate.value?.payment
    ?? paymentMatch.value?.payment
    ?? { status: paymentStatuses.pending }
})

const tgChats = computed(() => {
  if (matchedPaymentUpdate.value) {
    return Array.isArray(matchedPaymentUpdate.value.tg_chats)
      ? matchedPaymentUpdate.value.tg_chats
      : []
  }

  const fromSubscription = paymentMatch.value?.subscription?.tg_chats
  return Array.isArray(fromSubscription) ? fromSubscription : []
})

const tgChatLink = computed(() => tgChats.value[0]?.tg_link || null)

watch(() => subscriptions.value, () => {
  setTimeout(() => {
    state.isShown = true
  }, 50)
}, { deep: true, immediate: true })

async function getUserSubscriptions() {
  const res = await appAxios.subscription.userSubscriptions(userProfile.value.id)

  await store.commit('user/setProfile', {
    subscriptions: res.data.subscriptions,
  })
}

function close() {
  state.isShown = false
  store.dispatch('app/setMessageComponent', null)
}

async function checkSubscriptionStatus() {
  if (state.subscriptionLoading) return

  state.subscriptionLoading = true
  await getUserSubscriptions()
  setTimeout(() => {
    state.subscriptionLoading = false
  }, 50)
}

function handleFailedButtonClick() {
  state.isShown = false
  store.dispatch('app/setMessageComponent', null)
  router.push({ name: SUBSCRIPTION_CONTROL_ROUTE })
}

function handleButtonClick() {
  if (!tgChatLink.value) return
  window.open(tgChatLink.value, '_blank')
}

onBeforeMount(() => {
  getUserSubscriptions()
})
</script>

<style lang="scss" scoped>

.modal-divider {
  width: 100%;
  height: 1px;
  background: var(--app-bar-border-color);
  margin: 16px 0;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .gradient {
    position: absolute;
    width: 224px;
    height: 224px;
    left: -50%;
    top: -50%;

    &.success {
      background: radial-gradient(rgba(86, 231, 118, 0.35) 0%, rgba(88, 234, 120, 0.05) 25%, rgba(0, 0, 0, 0) 50%);
    }

    &.pending {
      background: radial-gradient(rgb(255, 153, 0, 0.35) 0%, rgba(255, 153, 0, 0.06) 25%, rgba(0, 0, 0, 0) 50%);
    }

    &.failed {
      background: radial-gradient(rgb(255, 48, 58, 0.35) 0%, rgba(255, 48, 58, 0.06) 25%, rgba(0, 0, 0, 0) 50%);
    }
  }

  .successIcon {
    position: relative;
    background-image: url("@/assets/icons/bigSuccess.svg");
    height: 112px;
    width: 112px;
  }

  .pendingIcon {
    position: relative;
    background-image: url("@/assets/icons/bigPending.svg");
    height: 112px;
    width: 112px;
  }

  .failedIcon {
    position: relative;
    background-image: url("@/assets/icons/bigFailed.svg");
    height: 112px;
    width: 112px;
  }
}
</style>
